import { useMemo } from 'react'

import {
  matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type {
  AgencyProviderModel, AgencyProviderRequestOptions,
} from '@models/types'

import type { ModuleState } from '@redux/modules/types'
import { EntitiesState } from '@redux/entities'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const watchEntityKeys = ['agencyProviders']

type HandleSort = {
  agencies: EntitiesState['agencies'],
  agencyProvider: AgencyProviderModel,
  providers: EntitiesState['providers'],
  sortKey: string,
}

const handleSort = (params: HandleSort) => {
  const {
    agencyProvider, sortKey, agencies, providers,
  } = params

  if (sortKey === 'provider_title'){
    return providers[agencyProvider.provider_id]?.title
  }

  return agencies[agencyProvider.agency_id]?.title
}

const groupProvidersByService = (
  agencyProviders: AgencyProviderModel[],
) => agencyProviders.reduce((serviceGroup, agencyProvider) => {
  const { service_id } = agencyProvider

  // @ts-expect-error TS7053
  if (!serviceGroup[service_id]){
    // @ts-expect-error TS7053
    serviceGroup[service_id] = []
  }

  // @ts-expect-error TS7053
  serviceGroup[service_id].push(agencyProvider)

  return serviceGroup
}, {})

type AgencyProviderFilters = {
  agency_id?: number,
  agency_string?: string,
  price_list_string?: string,
  provider_id?: number,
  provider_price_list_id?: number,
  provider_string?: string,
  service_id?: number,
}

type UseAgencyProvidersOptions = {
  entityKey?: string,
  filters?: AgencyProviderFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgencyProviderRequestOptions,
  sortKey?: string,
}

function useAgencyProviders(options: UseAgencyProvidersOptions) {
  const {
    entityKey, filters, requestOptions, sortKey = 'agency_title',
  } = options

  const {
    agency_id: filterAgencyId,
    agency_string: filterAgencyString,
    price_list_string: filterPriceListString,
    provider_id: filterProviderId,
    provider_price_list_id: filterPriceListId,
    provider_string: filterProviderString,
    service_id: filterServiceId,
  } = filters || {}

  const {
    updatedEntities: { agencyProviders: agencyProvidersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencies, agencyProviders, providers, providerPriceLists,
  } = entities || {}

  const filteredAgencyProviders = useMemo(() => {
    const filtered = Object.values(agencyProviders).filter((agencyProvider) => {
      const {
        agency_id, provider_id, provider_price_list_id, service_id,
      } = agencyProvider

      const provider = providers[provider_id] || {}
      const { title: providerTitle } = provider

      // @ts-expect-error TS2538
      const providerPriceList = providerPriceLists[provider_price_list_id] || {}
      const { title: providerPriceListTitle } = providerPriceList

      const agency = agencies[agency_id] || {}
      const { title: agencyTitle } = agency

      const matchAgencyIdString = agency_id.toString() === filterAgencyString
      const matchAgencyTitle = matchFilterString(agencyTitle, filterAgencyString)
      const matchAgencyString = matchAgencyIdString || matchAgencyTitle

      const matchProviderIdString = provider_id.toString() === filterProviderString
      const matchProviderTitle = matchFilterString(providerTitle, filterProviderString)
      const matchProviderString = matchProviderIdString || matchProviderTitle

      const matchAgencyId = matchFilterNumber(agency_id, filterAgencyId)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)

      const matchProviderPriceListIdString = provider_price_list_id?.toString() === filterPriceListString
      const matchProviderPriceListTitleString = matchFilterString(providerPriceListTitle, filterPriceListString)

      const matchProviderPriceListString = matchProviderPriceListTitleString || matchProviderPriceListIdString

      const matchProviderPriceListId = matchFilterNumber(provider_price_list_id, filterPriceListId)

      const matchServiceId = matchFilterNumber(service_id, filterServiceId)

      return matchAgencyId
        && matchAgencyString
        && matchProviderId
        && matchProviderPriceListId
        && matchProviderPriceListString
        && matchProviderString
        && matchServiceId
    })

    return sortArrayBy(filtered, 'asc', agencyProvider => handleSort({
      agencyProvider, sortKey, agencies, providers,
    }))
  }, [JSON.stringify(options), agencyProvidersUpdatedAt, JSON.stringify(filters)])

  const filteredAgencyProvidersCount = filteredAgencyProviders.length
  const hasFilteredAgencyProviders = !!filteredAgencyProvidersCount
  const groupedProvidersByService = hasFilteredAgencyProviders && groupProvidersByService(filteredAgencyProviders)

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgencyProvidersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgencyProviders } = useReduxAction(
    'agencyProviders',
    'loadAgencyProviders',
    {
      entityKey,
      ...defaultRequestOptions.agencyProvider,
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  // @ts-expect-error TS2802
  const uniqueProviderIds = [...new Set(filteredAgencyProviders.map(agencyProvider => agencyProvider.provider_id))]

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviders,
    filteredAgencyProvidersCount,
    groupedProvidersByService,
    hasFilteredAgencyProviders,
    loading: loadingAgencyProviders,
    uniqueProviderIds,
  }
}

export default useAgencyProviders
